import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  listIntegrations,
  updateIntegration,
  seedIntegrations,
} from 'service/AdminPanelService'

import styles from './IntegrationsPage.module.scss'

const STATUS_VARIANTS = {
  active: 'success',
  in_progress: 'warning',
  not_connected: 'danger',
  error: 'danger',
  not_required: 'muted',
}

const CATEGORY_KEYS = ['legal', 'government', 'payment', 'communication', 'education', 'infra']

const IntegrationsPage = () => {
  const { t } = useTranslation('common')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [editing, setEditing] = useState(null)
  const [draft, setDraft] = useState({})

  const load = async () => {
    setLoading(true)
    try {
      const res = await listIntegrations()
      const data = Array.isArray(res?.data) ? res.data : (res?.data?.results || [])
      setItems(data)
    } catch (e) {
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleSeed = async () => {
    try {
      await seedIntegrations()
      await load()
    } catch (e) {
      // noop
    }
  }

  const handleEdit = (item) => {
    setEditing(item.id)
    setDraft({
      status: item.status,
      provider_name: item.provider_name || '',
      provider_url: item.provider_url || '',
      provider_contact: item.provider_contact || '',
      responsible_name: item.responsible_name || '',
      responsible_contact: item.responsible_contact || '',
      notes: item.notes || '',
    })
  }

  const handleCancel = () => {
    setEditing(null)
    setDraft({})
  }

  const handleSave = async (id) => {
    try {
      await updateIntegration(id, draft)
      setEditing(null)
      setDraft({})
      await load()
    } catch (e) {
      // noop
    }
  }

  const filtered = filter === 'all' ? items : items.filter((i) => i.category === filter)

  const grouped = {}
  for (const it of filtered) {
    const c = it.category || 'infra'
    if (!grouped[c]) grouped[c] = []
    grouped[c].push(it)
  }

  const counts = items.reduce(
    (acc, it) => {
      acc.total += 1
      if (it.status === 'active') acc.active += 1
      else if (it.status === 'in_progress') acc.in_progress += 1
      else if (it.status === 'not_connected') acc.pending += 1
      return acc
    },
    { total: 0, active: 0, in_progress: 0, pending: 0 },
  )

  return (
    <div className={styles.page}>
      <div className={`unv-card ${styles.header}`}>
        <div>
          <h1 className={styles.title}>{t('integrations.title', 'Реестр внешних интеграций')}</h1>
          <div className={styles.subtitle}>
            {t(
              'integrations.subtitle',
              'Что нужно подключить для полноценной эксплуатации: КЭП, антиплагиат, банки, видеоконференции, мониторинг.',
            )}
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={`unv-btn ${styles.refreshBtn}`} onClick={load}>
            {t('common.refresh', 'Обновить')}
          </button>
          {items.length === 0 && !loading && (
            <button className={`unv-btn ${styles.seedBtn}`} onClick={handleSeed}>
              {t('integrations.seed', 'Засеять реестр')}
            </button>
          )}
        </div>
      </div>

      <div className={styles.summary}>
        <div className={`unv-card ${styles.summaryItem}`}>
          <div className={styles.summaryLabel}>{t('integrations.total', 'Всего')}</div>
          <div className={styles.summaryValue}>{counts.total}</div>
        </div>
        <div className={`unv-card ${styles.summaryItem} ${styles.success}`}>
          <div className={styles.summaryLabel}>{t('integrations.activeCount', 'Активны')}</div>
          <div className={styles.summaryValue}>{counts.active}</div>
        </div>
        <div className={`unv-card ${styles.summaryItem} ${styles.warning}`}>
          <div className={styles.summaryLabel}>{t('integrations.inProgressCount', 'В процессе')}</div>
          <div className={styles.summaryValue}>{counts.in_progress}</div>
        </div>
        <div className={`unv-card ${styles.summaryItem} ${styles.danger}`}>
          <div className={styles.summaryLabel}>{t('integrations.pendingCount', 'Не подключены')}</div>
          <div className={styles.summaryValue}>{counts.pending}</div>
        </div>
      </div>

      <div className={styles.filters}>
        <button
          className={`unv-btn ${filter === 'all' ? styles.filterActive : ''}`}
          onClick={() => setFilter('all')}
        >
          {t('integrations.all', 'Все')}
        </button>
        {CATEGORY_KEYS.map((k) => (
          <button
            key={k}
            className={`unv-btn ${filter === k ? styles.filterActive : ''}`}
            onClick={() => setFilter(k)}
          >
            {t(`integrations.cat_${k}`, k)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className={`unv-card ${styles.loadingState}`}>{t('common.loading', 'Загрузка...')}</div>
      ) : items.length === 0 ? (
        <div className={`unv-card ${styles.emptyState}`}>
          {t('integrations.empty', 'Реестр пуст. Нажмите «Засеять реестр», чтобы создать стандартные записи.')}
        </div>
      ) : (
        Object.keys(grouped).map((cat) => (
          <div key={cat} className={styles.categoryBlock}>
            <h2 className={styles.categoryTitle}>{t(`integrations.cat_${cat}`, cat)}</h2>
            <div className={styles.list}>
              {grouped[cat].map((item) => (
                <div key={item.id} className={`unv-card ${styles.card}`}>
                  <div className={styles.cardHead}>
                    <div className={styles.cardTitleBlock}>
                      <div className={styles.cardTitle}>
                        {item.title}
                        {item.is_required && (
                          <span className={styles.requiredBadge}>
                            {t('integrations.required', 'Обязательно')}
                          </span>
                        )}
                      </div>
                      <div className={styles.cardCode}>{item.code}</div>
                    </div>
                    <span className={`${styles.statusBadge} ${styles[STATUS_VARIANTS[item.status] || 'muted']}`}>
                      {item.status_display || item.status}
                    </span>
                  </div>

                  {item.description && <div className={styles.cardDesc}>{item.description}</div>}
                  {item.blocker_for && (
                    <div className={styles.cardBlocker}>
                      <strong>{t('integrations.blockerFor', 'Блокирует')}:</strong> {item.blocker_for}
                    </div>
                  )}

                  {editing === item.id ? (
                    <div className={styles.editForm}>
                      <label className={styles.field}>
                        <span>{t('integrations.status', 'Статус')}</span>
                        <select
                          value={draft.status}
                          onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                        >
                          <option value="not_connected">{t('integrations.s_not_connected', 'Не подключено')}</option>
                          <option value="in_progress">{t('integrations.s_in_progress', 'В процессе')}</option>
                          <option value="active">{t('integrations.s_active', 'Активно')}</option>
                          <option value="error">{t('integrations.s_error', 'Ошибка')}</option>
                          <option value="not_required">{t('integrations.s_not_required', 'Не требуется')}</option>
                        </select>
                      </label>
                      <label className={styles.field}>
                        <span>{t('integrations.providerName', 'Провайдер')}</span>
                        <input
                          type="text"
                          value={draft.provider_name}
                          onChange={(e) => setDraft({ ...draft, provider_name: e.target.value })}
                        />
                      </label>
                      <label className={styles.field}>
                        <span>{t('integrations.providerUrl', 'Сайт провайдера')}</span>
                        <input
                          type="url"
                          value={draft.provider_url}
                          onChange={(e) => setDraft({ ...draft, provider_url: e.target.value })}
                        />
                      </label>
                      <label className={styles.field}>
                        <span>{t('integrations.providerContact', 'Контакт провайдера')}</span>
                        <input
                          type="text"
                          value={draft.provider_contact}
                          onChange={(e) => setDraft({ ...draft, provider_contact: e.target.value })}
                        />
                      </label>
                      <label className={styles.field}>
                        <span>{t('integrations.responsibleName', 'Ответственный (ФИО)')}</span>
                        <input
                          type="text"
                          value={draft.responsible_name}
                          onChange={(e) => setDraft({ ...draft, responsible_name: e.target.value })}
                        />
                      </label>
                      <label className={styles.field}>
                        <span>{t('integrations.responsibleContact', 'Контакт ответственного')}</span>
                        <input
                          type="text"
                          value={draft.responsible_contact}
                          onChange={(e) => setDraft({ ...draft, responsible_contact: e.target.value })}
                        />
                      </label>
                      <label className={styles.field}>
                        <span>{t('integrations.notes', 'Заметки')}</span>
                        <textarea
                          rows={3}
                          value={draft.notes}
                          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                        />
                      </label>

                      <div className={styles.editActions}>
                        <button className={`unv-btn ${styles.saveBtn}`} onClick={() => handleSave(item.id)}>
                          {t('common.save', 'Сохранить')}
                        </button>
                        <button className={`unv-btn ${styles.cancelBtn}`} onClick={handleCancel}>
                          {t('common.cancel', 'Отмена')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={styles.metaRow}>
                        {item.provider_name && (
                          <div className={styles.meta}>
                            <span className={styles.metaLabel}>{t('integrations.provider', 'Провайдер')}:</span>
                            {item.provider_url ? (
                              <a className={styles.metaLink} href={item.provider_url} target="_blank" rel="noreferrer">
                                {item.provider_name}
                              </a>
                            ) : (
                              <span>{item.provider_name}</span>
                            )}
                          </div>
                        )}
                        {item.responsible_name && (
                          <div className={styles.meta}>
                            <span className={styles.metaLabel}>{t('integrations.responsible', 'Ответственный')}:</span>
                            <span>{item.responsible_name}</span>
                            {item.responsible_contact && <span className={styles.metaContact}>({item.responsible_contact})</span>}
                          </div>
                        )}
                        {item.notes && (
                          <div className={styles.meta}>
                            <span className={styles.metaLabel}>{t('integrations.notes', 'Заметки')}:</span>
                            <span>{item.notes}</span>
                          </div>
                        )}
                      </div>
                      <button className={`unv-btn ${styles.editBtn}`} onClick={() => handleEdit(item)}>
                        {t('common.edit', 'Изменить')}
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

export default IntegrationsPage
